import React from 'react'
import dayjs from 'dayjs'
import { useMutation } from '@tanstack/react-query'
import { ButtonBlock } from '@mobi/component-library/Common/Buttons'
import { NoticeBox } from '@core/Components/NoticeBox'
import { BankCardBlockStyled, InfoHeaderStyled, NoticeBoxStyled } from './Precommitment.styles'
import { Spinner } from '@mobi/component-library/Common/Spinner'
import { Grid, GridCell, GridRow } from '@mobi/component-library/Common/Grid'
import { CheckboxInput } from '@mobi/component-library/Common/Input/CheckboxInput'
import {
  PopupContainerStyled,
  PopupStyled,
  PopupTitleStyled,
} from '@core/Components/Popup/Popup.styles'
import type { PrecommitmentResponse } from '@mobi/account/Areas/SignUp/Utils/SignUpApi'
import { DEFAULT_COOLING_OFF_PERIOD_DAYS, PRECOMMITMENT_API_PATH_PREFIX } from './constants'
import { post } from '@classic/Foundation/Services/ApiService'
import { trackBlockDebitCard, trackUnblockDebitCard, trackCancelUnblockDebitCard } from './Utils/ga'

const BLOCK_BANK_CARD_URL = `${PRECOMMITMENT_API_PATH_PREFIX}UpdateCreditCardDeposits`

export const PrecommitmentBankCardBlock = ({
  data,
  hasDataFetchFailed,
}: {
  data: PrecommitmentResponse | undefined
  hasDataFetchFailed: boolean
}): JSX.Element => {
  const [isPopupShowing, setIsPopupShowing] = React.useState(false)
  const [precommitmentDetails, setPrecommitmentDetails] = React.useState(data)

  React.useEffect(() => {
    setPrecommitmentDetails(data)
  }, [data])

  return (
    <form id='credit-card-block'>
      {isPopupShowing && (
        <CardBlockPopup
          setIsPopupShowing={setIsPopupShowing}
          setPrecommitmentDetails={setPrecommitmentDetails}
          precommitmentDetails={precommitmentDetails}
        />
      )}
      <InfoHeaderStyled level={3}>Block Debit Card</InfoHeaderStyled>
      <BankCardBlockStyled>
        {hasDataFetchFailed && (
          <NoticeBox
            title='Unable to display your debit card status. Please try again later.'
            noticeBoxType='Warning'
            hasBorder
            data-tid-fetch-failed=''
          />
        )}
        {!hasDataFetchFailed && (
          <>
            <NoticeBox
              title={
                precommitmentDetails?.BlockCreditCardDeposits
                  ? precommitmentDetails?.ProposedCreditCardReleaseDate
                    ? `Your debit card block will be removed on ${precommitmentDetails?.ProposedCreditCardReleaseDate}.`
                    : 'Debit card deposits are currently blocked.'
                  : 'Debit card deposits are not currently blocked.'
              }
              noticeBoxType={
                precommitmentDetails?.BlockCreditCardDeposits
                  ? precommitmentDetails?.ProposedCreditCardReleaseDate
                    ? 'Warning'
                    : 'Error'
                  : 'Info'
              }
              hasBorder
            />
            <ButtonBlock
              id='credit-card-block-button'
              type='button'
              onClick={() => {
                if (
                  precommitmentDetails?.BlockCreditCardDeposits &&
                  precommitmentDetails?.ProposedCreditCardReleaseDate
                ) {
                  trackCancelUnblockDebitCard()
                }
                setIsPopupShowing(true)
              }}
            >
              {precommitmentDetails?.BlockCreditCardDeposits
                ? precommitmentDetails?.ProposedCreditCardReleaseDate
                  ? 'Cancel Unblock'
                  : 'Remove Block'
                : 'Block'}
            </ButtonBlock>
          </>
        )}
      </BankCardBlockStyled>
    </form>
  )
}

type CardBlockPopupProps = {
  setIsPopupShowing: (x: boolean) => void
  setPrecommitmentDetails: (x: PrecommitmentResponse) => void
  precommitmentDetails?: PrecommitmentResponse
}

const CardBlockPopup: React.FC<CardBlockPopupProps> = ({
  setIsPopupShowing,
  setPrecommitmentDetails,
  precommitmentDetails,
}) => {
  const [hasAgreed, setHasAgreed] = React.useState(false)

  const willUnblock =
    precommitmentDetails?.BlockCreditCardDeposits &&
    !precommitmentDetails.ProposedCreditCardReleaseDate

  const mutation = useMutation({
    mutationFn: () =>
      post<PrecommitmentResponse>({
        url: BLOCK_BANK_CARD_URL,
        body: !willUnblock,
      }),
    onSuccess: data => {
      if (willUnblock) {
        trackUnblockDebitCard()
      } else {
        trackBlockDebitCard()
      }
      setIsPopupShowing(false)
      setPrecommitmentDetails(data)
    },
  })

  return (
    <PopupContainerStyled id='bet-limit-credit-card-block-popup'>
      <PopupStyled>
        {mutation.isPending && <Spinner />}
        <Grid>
          <GridRow>
            <GridCell align='center'>
              <PopupTitleStyled id='credit-card-block-title'>
                {willUnblock ? 'Remove Debit Card Block' : 'Block Debit Card'}
              </PopupTitleStyled>
            </GridCell>
          </GridRow>
          {!willUnblock && (
            <>
              <GridRow>
                <GridCell align='left' padding='2em 0 0'>
                  <strong>By proceeding:</strong>
                </GridCell>
              </GridRow>
              <GridRow>
                <GridCell align='left' data-tid-cooling-explanation padding='0 0 1em'>
                  I understand that debit card deposits to my account will be blocked effective
                  immediately. If I choose to remove this block in future, it will be subject to a{' '}
                  {precommitmentDetails?.CoolingOffPeriodDays || DEFAULT_COOLING_OFF_PERIOD_DAYS}{' '}
                  day cooling off period.
                </GridCell>
              </GridRow>
            </>
          )}
          {willUnblock && (
            <GridRow>
              <NoticeBoxStyled>
                <NoticeBox title='' noticeBoxType='Warning' hasBorder>
                  Your debit card block will be{' '}
                  <strong>
                    removed on{' '}
                    {dayjs()
                      .add(
                        precommitmentDetails?.CoolingOffPeriodDays ||
                          DEFAULT_COOLING_OFF_PERIOD_DAYS,
                        'days'
                      )
                      .format('DD/MM/YYYY')}
                  </strong>
                  , after the mandatory cooling off period.
                </NoticeBox>
              </NoticeBoxStyled>
            </GridRow>
          )}
          <GridRow>
            <CheckboxInput
              label='I understand and accept.'
              id='confirm-changes-checkbox'
              checked={hasAgreed}
              onChange={x => setHasAgreed(x.target.checked)}
              data-tid-confirm=''
            />
          </GridRow>
          <GridRow>
            <GridCell padding='2em 0.5em 0 0'>
              <ButtonBlock
                id='credit-card-block-cancel'
                color='secondary'
                type='button'
                onClick={() => setIsPopupShowing(false)}
              >
                Cancel
              </ButtonBlock>
            </GridCell>
            <GridCell padding='2em 0 0 0.5em'>
              <ButtonBlock
                id='credit-card-block-save'
                color='primary'
                type='button'
                disabled={mutation.isPending || !hasAgreed}
                onClick={() => mutation.mutate()}
              >
                Save
              </ButtonBlock>
            </GridCell>
          </GridRow>
        </Grid>
      </PopupStyled>
    </PopupContainerStyled>
  )
}
